import {
  Patient,
  Practitioner,
  ServiceRequest,
  Specimen,
  HumanName,
  DiagnosticReport,
} from '@medplum/fhirtypes';
import { ReportBindingData } from '../types/template.types';
import { IDENTIFIERS } from '@/consts/systems.consts';

/**
 * Extract display name from HumanName
 * @param name - Array of FHIR HumanName objects
 * @returns Formatted display name string
 */
function getDisplayName(name?: HumanName[]): string {
  if (!name || name.length === 0) {
    return '';
  }

  const primaryName = name[0];

  // Use text if available
  if (primaryName.text) {
    return primaryName.text;
  }

  // Construct from parts
  const parts: string[] = [];

  if (primaryName.prefix) {
    parts.push(...primaryName.prefix);
  }

  if (primaryName.given) {
    parts.push(...primaryName.given);
  }

  if (primaryName.family) {
    parts.push(primaryName.family);
  }

  if (primaryName.suffix) {
    parts.push(...primaryName.suffix);
  }

  return parts.join(' ');
}

/**
 * Extract AMKA (Greek social security number) from patient identifiers
 * @param patient - FHIR Patient resource
 * @returns AMKA identifier value or undefined
 */
function getAMKA(patient?: Patient): string | undefined {
  if (!patient?.identifier) {
    return undefined;
  }

  // Look for AMKA identifier
  const amkaIdentifier = patient.identifier.find(
    (id) => id.system === IDENTIFIERS.AMKA
  );

  return amkaIdentifier?.value;
}

/**
 * Extract order identifier from ServiceRequest
 * @param serviceRequest - FHIR ServiceRequest resource
 * @returns Order identifier value or undefined
 */
function getOrderIdentifier(
  serviceRequest?: ServiceRequest
): string | undefined {
  if (!serviceRequest?.identifier) {
    return undefined;
  }

  // Return first identifier value or accession number
  const accession = serviceRequest.identifier.find((id) =>
    id.type?.coding?.some((c) => c.code === 'ACSN')
  );

  return accession?.value || serviceRequest.identifier[0]?.value;
}

/**
 * Get specimen type display text
 * @param specimen - FHIR Specimen resource
 * @returns Specimen type display text or undefined
 */
function getSpecimenType(specimen?: Specimen): string | undefined {
  if (!specimen?.type) {
    return undefined;
  }

  // Use text if available
  if (specimen.type.text) {
    return specimen.type.text;
  }

  // Use first coding display
  const coding = specimen.type.coding?.[0];
  return coding?.display || coding?.code;
}

/**
 * Format date string to display format
 * @param dateString - ISO date string to format
 * @returns Formatted date string or undefined
 */
function formatDateForDisplay(dateString?: string): string | undefined {
  if (!dateString) {
    return undefined;
  }

  try {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  } catch {
    return dateString;
  }
}

/**
 * Populate patient information from FHIR Patient resource
 * @param patient - FHIR Patient resource
 * @returns Patient binding data for report template
 */
export function populatePatientInfo(
  patient?: Patient
): ReportBindingData['patient'] {
  return {
    id: patient?.id || '',
    name: getDisplayName(patient?.name),
    birthDate: patient?.birthDate,
    gender: patient?.gender,
    identifier: getAMKA(patient),
  };
}

/**
 * Populate order information from FHIR ServiceRequest resource
 * @param serviceRequest - FHIR ServiceRequest resource
 * @param requester - FHIR Practitioner who requested the order
 * @returns Order binding data for report template
 */
export function populateOrderInfo(
  serviceRequest?: ServiceRequest,
  requester?: Practitioner
): ReportBindingData['order'] {
  // Extract test name from code
  const testName =
    serviceRequest?.code?.text ||
    serviceRequest?.code?.coding?.[0]?.display ||
    undefined;

  const reasonCode =
    serviceRequest?.reasonCode?.[0].coding?.[0].display ||
    serviceRequest?.reasonCode?.[0].text ||
    undefined;

  return {
    id: serviceRequest?.id || '',
    identifier: getOrderIdentifier(serviceRequest),
    orderDate:
      formatDateForDisplay(serviceRequest?.authoredOn) ||
      new Date().toISOString(),
    requesterName: getDisplayName(requester?.name),
    testName,
    reasonCode,
  };
}

/**
 * Populate specimen information from FHIR Specimen resource
 * @param specimen - FHIR Specimen resource
 * @returns Specimen binding data for report template or undefined
 */
export function populateSpecimenInfo(
  specimen?: Specimen
): ReportBindingData['specimen'] | undefined {
  if (!specimen) {
    return undefined;
  }

  return {
    id: specimen.id || '',
    type: getSpecimenType(specimen),
    collectionDate: formatDateForDisplay(
      specimen.collection?.collectedDateTime as string | undefined
    ),
    receivedDate: formatDateForDisplay(specimen.receivedTime),
  };
}

/**
 * Create complete binding data from order details
 * @param serviceRequest - FHIR ServiceRequest resource
 * @param patient - FHIR Patient resource
 * @param requester - FHIR Practitioner who requested the order
 * @param specimens - Array of FHIR Specimen resources
 * @param report - FHIR DiagnosticReport resource
 * @returns Complete binding data for report template
 */
export function createReportBindingData(
  serviceRequest?: ServiceRequest,
  patient?: Patient,
  requester?: Practitioner,
  specimens?: Specimen[],
  report?: DiagnosticReport | null
): ReportBindingData {
  // Use first specimen if available
  const primarySpecimen =
    specimens && specimens.length > 0 ? specimens[0] : undefined;

  return {
    patient: populatePatientInfo(patient),
    order: populateOrderInfo(serviceRequest, requester),
    specimen: populateSpecimenInfo(primarySpecimen),
    report: {
      reportDate: report?.issued ?? undefined,
    },
  };
}
